// /ugc-builder — DEV-ONLY curator for Fake UGC. Drop a UGC aesthetic reference
// (a real phone-shot creator photo), vision-extract a realism-first recipe,
// tweak it, and save to the backend UGC store. Backend-only styles — never shown
// to customers. Also a test-render so you can A/B the aesthetic before it goes
// live. Gated by import.meta.env.DEV → tree-shaken from prod builds.
// Backend: /api/dev/ugc/* (routes/dev.py).

import { useEffect, useState } from 'react';
import {
  extractUgcRecipe,
  saveUgcReference,
  listUgcReferences,
  deleteUgcReference,
  testRenderUgc,
  listMoodboardsUntagged,
  importMoodboardToUgc,
  type UgcRecipe,
  type UgcReference,
  type ImportableMoodboard,
} from '@/lib/api';

function fileToBase64(file: File): Promise<{ b64: string; mime: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || '');
      const comma = s.indexOf(',');
      resolve({ b64: comma >= 0 ? s.slice(comma + 1) : s, mime: file.type || 'image/webp' });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const EMPTY_RECIPE: UgcRecipe = {
  lighting: '', setting: '', camera: '', composition: '', mood: '', color_tone: '',
  text_overlay: { present: false, style: '', sample: '' },
};

export default function UgcBuilderPage() {
  if (!import.meta.env.DEV) return <div className="p-10 text-center text-dark-muted">Dev only.</div>;

  const [refImg, setRefImg] = useState<{ b64: string; mime: string; preview: string } | null>(null);
  const [name, setName] = useState('');
  const [recipe, setRecipe] = useState<UgcRecipe>(EMPTY_RECIPE);
  const [busy, setBusy] = useState<'' | 'extract' | 'save' | 'render'>('');
  const [saved, setSaved] = useState<UgcReference[]>([]);
  const [msg, setMsg] = useState('');

  // Test-render state
  const [prodImg, setProdImg] = useState<{ b64: string; mime: string; preview: string } | null>(null);
  const [prodName, setProdName] = useState('the product');
  const [renderOut, setRenderOut] = useState<string>('');

  // Import-from-moodboards state
  const [importable, setImportable] = useState<ImportableMoodboard[]>([]);
  const [importingId, setImportingId] = useState('');

  const loadSaved = () => { listUgcReferences().then(setSaved).catch(() => setSaved([])); };
  const loadImportable = () => { listMoodboardsUntagged().then(setImportable).catch(() => setImportable([])); };
  useEffect(() => { loadSaved(); loadImportable(); }, []);

  const importFromMoodboard = async (m: ImportableMoodboard) => {
    setImportingId(m.id); setMsg('');
    try {
      const r = await importMoodboardToUgc(m.image_url);
      setRefImg({ b64: r.image_b64, mime: r.mime, preview: m.image_url });
      setRecipe({ ...EMPTY_RECIPE, ...r.recipe, text_overlay: { ...EMPTY_RECIPE.text_overlay, ...(r.recipe.text_overlay || {}) } });
      setName('');
      setMsg('Imported + extracted from moodboard. Review + tweak, then Save.');
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Import failed'); }
    finally { setImportingId(''); }
  };

  const onRefFile = async (f: File | undefined) => {
    if (!f) return;
    const { b64, mime } = await fileToBase64(f);
    setRefImg({ b64, mime, preview: URL.createObjectURL(f) });
  };
  const onProdFile = async (f: File | undefined) => {
    if (!f) return;
    const { b64, mime } = await fileToBase64(f);
    setProdImg({ b64, mime, preview: URL.createObjectURL(f) });
  };

  const extract = async () => {
    if (!refImg) return;
    setBusy('extract'); setMsg('');
    try {
      const r = await extractUgcRecipe(refImg.b64, refImg.mime);
      setRecipe({ ...EMPTY_RECIPE, ...r, text_overlay: { ...EMPTY_RECIPE.text_overlay, ...(r.text_overlay || {}) } });
      setMsg('Extracted. Review + tweak, then Save.');
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Extract failed'); }
    finally { setBusy(''); }
  };

  const save = async () => {
    if (!refImg) return;
    setBusy('save'); setMsg('');
    try {
      await saveUgcReference(refImg.b64, recipe, name, refImg.mime);
      setMsg('Saved to UGC store.');
      setRefImg(null); setName(''); setRecipe(EMPTY_RECIPE);
      loadSaved();
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Save failed'); }
    finally { setBusy(''); }
  };

  const doDelete = async (id: string) => {
    await deleteUgcReference(id).catch(() => {});
    loadSaved();
  };

  const testRender = async () => {
    if (!prodImg) { setMsg('Add a product image to test-render.'); return; }
    setBusy('render'); setMsg(''); setRenderOut('');
    try {
      const r = await testRenderUgc(recipe, prodImg.b64, prodImg.mime, prodName);
      setRenderOut(`data:image/png;base64,${r.image_b64}`);
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Render failed'); }
    finally { setBusy(''); }
  };

  const field = (key: keyof UgcRecipe, label: string, rows = 2) => (
    <label className="block mb-3">
      <span className="text-xs font-bold text-dark-muted uppercase tracking-wide">{label}</span>
      <textarea
        value={String(recipe[key] ?? '')}
        onChange={(e) => setRecipe((r) => ({ ...r, [key]: e.target.value }))}
        rows={rows}
        className="mt-1 w-full text-sm border border-dark-muted/40 rounded-lg px-3 py-2 focus:outline-none focus:border-primary"
      />
    </label>
  );

  return (
    <div className="max-w-[1200px] mx-auto p-6">
      <h1 className="text-2xl font-bold text-dark mb-1">UGC Builder <span className="text-xs font-normal text-amber-600">(dev)</span></h1>
      <p className="text-sm text-dark-muted mb-6">
        Drop a UGC aesthetic reference → extract a realism-first recipe (lighting-led, no model identity) → save. Backend-only.
      </p>
      {msg && <div className="mb-4 px-4 py-2 rounded-lg bg-surface text-sm text-dark">{msg}</div>}

      {/* IMPORT FROM MOODBOARDS — untagged only. Click one → server fetches +
          extracts → populates the review form below. Import = copy (the source
          moodboard stays; delete it from the moodboard builder if you want). */}
      <div className="rounded-2xl border border-surface-dark/40 bg-white p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold uppercase tracking-wide text-dark">
            Import from Moodboards <span className="text-dark-muted font-normal normal-case">(untagged only · {importable.length})</span>
          </h2>
          <button onClick={loadImportable} className="text-xs text-primary font-semibold">Refresh</button>
        </div>
        {importable.length === 0 ? (
          <p className="text-sm text-dark-muted">No untagged moodboards found. Screenshot-copy some into the moodboard builder first.</p>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
            {importable.map((m) => (
              <button
                key={m.id}
                onClick={() => importFromMoodboard(m)}
                disabled={!!importingId}
                title={m.style_summary || 'Import to UGC'}
                className="relative rounded-lg border border-surface-dark/30 overflow-hidden hover:border-primary transition-colors disabled:opacity-50"
              >
                <img src={m.image_url} alt="moodboard" className="w-full aspect-square object-cover" />
                {importingId === m.id && (
                  <span className="absolute inset-0 bg-black/50 text-white text-[10px] flex items-center justify-center">Importing…</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT: extract + edit + save */}
        <div className="rounded-2xl border border-surface-dark/40 bg-white p-5">
          <h2 className="text-sm font-bold uppercase tracking-wide text-dark mb-3">1 · Reference → recipe</h2>
          <input type="file" accept="image/*" onChange={(e) => onRefFile(e.target.files?.[0])} className="text-sm mb-3" />
          {refImg && <img src={refImg.preview} alt="ref" className="w-40 h-40 object-cover rounded-lg border mb-3" />}
          <div className="flex gap-2 mb-4">
            <button disabled={!refImg || busy === 'extract'} onClick={extract}
              className="px-4 py-2 rounded-full bg-primary text-white text-sm font-bold disabled:opacity-40">
              {busy === 'extract' ? 'Extracting…' : 'Extract recipe'}
            </button>
          </div>

          <label className="block mb-3">
            <span className="text-xs font-bold text-dark-muted uppercase tracking-wide">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full text-sm border border-dark-muted/40 rounded-lg px-3 py-2 focus:outline-none focus:border-primary" />
          </label>
          {field('lighting', 'Lighting (most important)', 3)}
          {field('setting', 'Setting')}
          {field('camera', 'Camera realism')}
          {field('composition', 'Composition')}
          {field('mood', 'Mood', 1)}
          {field('color_tone', 'Color tone', 1)}
          <label className="flex items-center gap-2 mb-2 text-sm">
            <input type="checkbox"
              checked={!!recipe.text_overlay?.present}
              onChange={(e) => setRecipe((r) => ({ ...r, text_overlay: { ...(r.text_overlay || {}), present: e.target.checked } }))} />
            Text overlay present
          </label>
          {recipe.text_overlay?.present && (
            <>
              <label className="block mb-3">
                <span className="text-xs font-bold text-dark-muted uppercase tracking-wide">Caption style</span>
                <input value={recipe.text_overlay?.style || ''}
                  onChange={(e) => setRecipe((r) => ({ ...r, text_overlay: { ...(r.text_overlay || {}), style: e.target.value } }))}
                  className="mt-1 w-full text-sm border border-dark-muted/40 rounded-lg px-3 py-2 focus:outline-none focus:border-primary" />
              </label>
              <label className="block mb-3">
                <span className="text-xs font-bold text-dark-muted uppercase tracking-wide">Sample caption</span>
                <input value={recipe.text_overlay?.sample || ''}
                  onChange={(e) => setRecipe((r) => ({ ...r, text_overlay: { ...(r.text_overlay || {}), sample: e.target.value } }))}
                  className="mt-1 w-full text-sm border border-dark-muted/40 rounded-lg px-3 py-2 focus:outline-none focus:border-primary" />
              </label>
            </>
          )}
          <button disabled={!refImg || busy === 'save'} onClick={save}
            className="mt-2 px-5 py-2 rounded-full bg-emerald-600 text-white text-sm font-bold disabled:opacity-40">
            {busy === 'save' ? 'Saving…' : 'Save UGC style'}
          </button>
        </div>

        {/* RIGHT: test render + saved list */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-surface-dark/40 bg-white p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-dark mb-3">2 · Test render (no save)</h2>
            <input type="file" accept="image/*" onChange={(e) => onProdFile(e.target.files?.[0])} className="text-sm mb-2" />
            <input value={prodName} onChange={(e) => setProdName(e.target.value)} placeholder="Product name"
              className="block w-full text-sm border border-dark-muted/40 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:border-primary" />
            <button disabled={!prodImg || busy === 'render'} onClick={testRender}
              className="px-4 py-2 rounded-full bg-primary text-white text-sm font-bold disabled:opacity-40">
              {busy === 'render' ? 'Rendering…' : 'Test render with current recipe'}
            </button>
            {renderOut && <img src={renderOut} alt="render" className="mt-4 w-64 h-64 object-cover rounded-lg border" />}
          </div>

          <div className="rounded-2xl border border-surface-dark/40 bg-white p-5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-dark mb-3">Saved UGC styles ({saved.length})</h2>
            <div className="grid grid-cols-3 gap-3">
              {saved.map((s) => (
                <div key={s.id} className="relative rounded-lg border overflow-hidden group">
                  <img src={s.image_url} alt={s.name} className="w-full aspect-square object-cover" />
                  <div className="px-2 py-1 text-[11px] truncate">{s.name}</div>
                  <button onClick={() => doDelete(s.id)}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs opacity-0 group-hover:opacity-100">✕</button>
                </div>
              ))}
              {saved.length === 0 && <p className="text-sm text-dark-muted col-span-3">None yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
